// mindmetal-app/web/src/pages/employee/mindmetal/HumanReviewTab.tsx
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ClipboardCheck,
  CheckCircle2,
  AlertTriangle,
  ShieldCheck,
  Inbox,
} from 'lucide-react'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  EmptyState,
  api,
} from '@personic/shared'

interface ReviewItem {
  profile_id: string
  canonical_id: string
  coverage_level: string
  stability_indicator: string
  reason: string
  tenant_name: string
}

interface ReviewQueueResponse {
  items: ReviewItem[]
}

const COVERAGE_VARIANT: Record<string, 'default' | 'warning' | 'success' | 'sage'> = {
  minimal: 'default',
  emerging: 'warning',
  established: 'success',
  mature: 'sage',
}

const STABILITY_VARIANT: Record<string, 'default' | 'warning' | 'sage' | 'error'> = {
  volatile: 'error',
  stabilizing: 'warning',
  stable: 'sage',
}

export function HumanReviewTab() {
  const queryClient = useQueryClient()
  const [approvedIds, setApprovedIds] = useState<Set<string>>(new Set())

  // Fetch the review queue
  const { data, isLoading, error } = useQuery({
    queryKey: ['mindmetal-review-queue'],
    queryFn: async () => {
      const { data } = await api.get<ReviewQueueResponse>('/admin/mindmetal/review-queue')
      return data
    },
  })

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: async (profileId: string) => {
      await api.post(`/admin/mindmetal/review-queue/${profileId}/approve`)
      return profileId
    },
    onSuccess: (profileId) => {
      setApprovedIds((prev) => new Set([...prev, profileId]))
      // Refetch the queue after a brief delay to let the toast show
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['mindmetal-review-queue'] })
      }, 1500)
    },
  })

  const items = data?.items || []
  const pendingItems = items.filter((item) => !approvedIds.has(item.profile_id))

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-copper-600" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
        Failed to load review queue:{' '}
        {(error as any)?.response?.data?.detail || (error as Error).message}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-copper-600" />
            Human Review Queue
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-warm-500">
            Profiles flagged for human review based on stability, coverage anomalies, or automated
            checks. Approve profiles to clear them from the queue.
          </p>
          <div className="flex items-center gap-4 mt-4">
            <div className="bg-copper-50 rounded-lg px-4 py-2 text-center">
              <div className="text-2xl font-bold text-copper-700">{pendingItems.length}</div>
              <div className="text-xs text-copper-600">Pending Review</div>
            </div>
            <div className="bg-sage-50 rounded-lg px-4 py-2 text-center">
              <div className="text-2xl font-bold text-sage-700">{approvedIds.size}</div>
              <div className="text-xs text-sage-600">Approved This Session</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Success toast for approvals */}
      <AnimatePresence>
        {approveMutation.isSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-4 bg-sage-50 border border-sage-200 rounded-xl text-sm text-sage-700 flex items-center gap-2"
          >
            <CheckCircle2 className="w-4 h-4" />
            Profile approved successfully
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error on approve */}
      {approveMutation.isError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          Failed to approve:{' '}
          {(approveMutation.error as any)?.response?.data?.detail ||
            (approveMutation.error as Error).message}
        </div>
      )}

      {/* Empty state */}
      {pendingItems.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Inbox className="w-8 h-8" />}
            title="Review Queue Empty"
            description="No profiles are currently flagged for human review. All profiles are passing automated checks."
          />
        </Card>
      ) : (
        /* Review table */
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-warm-200">
                  <th className="text-left text-xs font-heading font-medium text-warm-500 uppercase tracking-wider px-6 py-4">
                    Canonical ID
                  </th>
                  <th className="text-left text-xs font-heading font-medium text-warm-500 uppercase tracking-wider px-6 py-4">
                    Tenant
                  </th>
                  <th className="text-left text-xs font-heading font-medium text-warm-500 uppercase tracking-wider px-6 py-4">
                    Coverage
                  </th>
                  <th className="text-left text-xs font-heading font-medium text-warm-500 uppercase tracking-wider px-6 py-4">
                    Stability
                  </th>
                  <th className="text-left text-xs font-heading font-medium text-warm-500 uppercase tracking-wider px-6 py-4">
                    Reason
                  </th>
                  <th className="text-right text-xs font-heading font-medium text-warm-500 uppercase tracking-wider px-6 py-4">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {pendingItems.map((item, i) => (
                    <motion.tr
                      key={item.profile_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ delay: i * 0.03 }}
                      className="border-b border-warm-100 hover:bg-warm-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <span className="font-mono text-sm text-copper-600">
                          {item.canonical_id}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-midnight-800">{item.tenant_name}</span>
                      </td>
                      <td className="px-6 py-4">
                        <Badge
                          variant={COVERAGE_VARIANT[item.coverage_level] || 'default'}
                          size="sm"
                        >
                          {item.coverage_level}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <Badge
                          variant={STABILITY_VARIANT[item.stability_indicator] || 'default'}
                          size="sm"
                        >
                          {item.stability_indicator}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-warm-600 max-w-xs truncate block">
                          {item.reason}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => approveMutation.mutate(item.profile_id)}
                          disabled={approveMutation.isPending}
                          leftIcon={<ShieldCheck className="w-4 h-4" />}
                        >
                          Approve
                        </Button>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  )
}
